(function () {
  'use strict';

  // Schema source of truth: UserFiles/HANDOFF.md
  // Each product entry in the JSON has these short keys:
  //   h  handle, n  name, s  set code, sn  set name, c  card number,
  //   r  rarity, t  supertype (Pokémon|Trainer), pt  print type,
  //   p  price in cents AUD (LOWEST in-stock variant), i  in stock,
  //   nv in-stock variant count (0 → sold out, 1 → single price, 2+ → FROM)
  // Non-card rows (sealed/graded), appended after all singles, carry only
  // h/n/p/i/nv/img plus:
  //   k  kind ("sealed"|"graded"), ty  product type ("Booster Box", "ETB", …)

  // Bump this key whenever the JSON schema OR the set of distinct values in
  // a categorical field (e.g. `e` / series) changes upstream, so cached
  // copies in users' sessionStorage are invalidated on next page load
  // instead of serving stale data forever.
  // v2: 2026-05-17 — series merged from 17→13 values (WOTC, Promo buckets).
  // v3: 2026-05-17 — Base folded into WOTC (12 values); payload now ships a
  //                  top-level series_order field; theme reads it instead of
  //                  hard-coding the order.
  // v4: 2026-08-02 — feed appends sealed/graded k-rows (new `k`/`ty` fields,
  //                  no card facet fields) after the singles.
  var SESSION_KEY = 'manganoSinglesIndex_v4';

  // Badge shown on k-rows that carry no `b` set-badge URL (all graded, and
  // sealed until the feed emits theirs). Populated at boot from the section's
  // data-badge-placeholder attribute ({{ 'badge-pokemon-default.png' | asset_url }}).
  var BADGE_PLACEHOLDER = '';

  // Bucket definitions — confirm with Jed before launch (see HANDOFF).
  var RARITY_BUCKETS = [
    { label: 'Common',           values: ['Common', '1 Diamond'] },
    { label: 'Uncommon',         values: ['Uncommon', '2 Diamond'] },
    { label: 'Rare',             values: ['Rare', 'Rare Holo', '3 Diamond', '1 Star'] },
    { label: 'Ultra Rare',       values: ['Rare Ultra', 'Rare Holo EX', 'Rare Holo V', 'Rare Holo GX', 'Ultra Rare', 'Double Rare', '4 Diamond', '2 Star', 'ACE SPEC Rare'] },
    { label: 'Secret / Special', values: ['Rare Secret', 'Rare Rainbow', 'Hyper Rare', 'Special Illustration Rare', 'Illustration Rare', 'Shiny Rare', 'Rare Shiny', '3 Star'] },
    { label: 'Promo',            values: ['Promo', 'Pocket Promo', 'Black Star Promo'] }
  ];

  function rarityBucket(r) {
    if (!r) return 'Other';
    for (var i = 0; i < RARITY_BUCKETS.length; i++) {
      if (RARITY_BUCKETS[i].values.indexOf(r) !== -1) return RARITY_BUCKETS[i].label;
    }
    return 'Other';
  }

  function printTypeBucket(pt) {
    if (!pt) return 'Other';
    if (/^1st Edition/i.test(pt)) return '1st Edition';
    if (/Shadowless/i.test(pt))   return 'Shadowless';
    if (/Reverse Holo/i.test(pt)) return 'Reverse Holo';
    if (/Holo/i.test(pt))         return 'Holo';
    if (/Non-Holo/i.test(pt) || /^Unlimited/i.test(pt)) return 'Non-Holo';
    return 'Special';
  }

  // Bucket for the search-mode "Product type" facet: card rows (no `k`)
  // split by language — English cards are Singles, `l:"ja"` rows are
  // Japanese; k-rows split by kind.
  function productTypeBucket(p) {
    if (!p.k) return p.l === 'ja' ? 'Japanese' : 'Singles';
    return p.k === 'graded' ? 'Graded' : 'Sealed';
  }

  // Compact display of the print type for the card metadata row — keeps
  // long phrasings ("1st Edition Shadowless Holo") from wrapping awkwardly.
  function printTypeAbbrev(pt) {
    if (!pt) return '';
    return pt
      .replace(/1st Edition/gi, '1st Ed')
      .replace(/Shadowless/gi, 'SL')
      .replace(/Reverse Holo/gi, 'Rev Holo')
      .replace(/Unlimited /gi, 'Unl ')
      .replace(/Cosmos Holo/gi, 'Cosmos')
      .replace(/Cracked Ice Holo/gi, 'Cracked Ice');
  }

  var PRICE_BUCKETS = [
    { label: '$0 – $5',     min: 0,      max: 500 },
    { label: '$5 – $20',    min: 500,    max: 2000 },
    { label: '$20 – $100',  min: 2000,   max: 10000 },
    { label: '$100 – $500', min: 10000,  max: 50000 },
    { label: '$500+',       min: 50000,  max: Infinity }
  ];

  // The JSON payload now ships a top-level `series_order` array (see
  // HANDOFF.md schema, "Top-level fields") — pre-sorted newest era → oldest
  // with "Promo" pinned to the end, auto-derived from each merged series'
  // newest set release_date. Storefront uses payload.series_order directly,
  // so when a new TCG generation ships the order updates on its own with
  // no JS change. This constant is a **fallback only** for the unlikely
  // case where the payload arrives without `series_order` (e.g. a partial
  // build, or a stale cached payload from before the field shipped). Keep
  // it aligned with the payload convention: newest → oldest, Promo last.
  var SERIES_ORDER_FALLBACK = [
    'Mega Evolution',
    'Scarlet & Violet',
    'Sword & Shield',
    'Sun & Moon',
    'XY',
    'Black & White',
    'HeartGold & SoulSilver',
    'Platinum',
    'Diamond & Pearl',
    'EX',
    'WOTC',
    'Promo'
  ];

  function formatPrice(cents) {
    if (cents == null) return '';
    return '$' + (cents / 100).toFixed(2);
  }

  // Shopify CDN supports `?width=N` for on-the-fly resizing. Cards render
  // at ~200px wide on desktop, so a 400px source covers most density-1
  // displays and a 800px srcset entry handles retina. Original files are
  // ~80KB at 1000×1200; a 400px variant is ~10–15KB — ~5× faster to fetch,
  // which is the difference between "first paint after pagination feels
  // instant" vs "blank cards for a beat". R2 fallback URLs (a tiny number
  // of in-flight rows per HANDOFF.md) don't support the param, so we only
  // apply the transform to cdn.shopify.com origins.
  function shopifyImageWidth(url, width) {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Live pixel offset used to park the search/sort bar near the top of the
  // viewport after a page change. The product masthead is hidden on these
  // pages, so .collection-search-wrapper is the first content; we land it just
  // below where the header sits. We only RESERVE the header's height when the
  // header is actually sticky/fixed (then it overlaps content) — with the
  // current non-sticky header (sticky_header off) it scrolls away, so we just
  // leave a small breathing gap. Measured live rather than via --header-height
  // (that var is only set by the homepage slideshow JS, so it's unset here).
  function stickyHeaderOffset() {
    var GAP = 16;
    var h = document.querySelector('.site-header');
    if (h) {
      var pos = getComputedStyle(h).position;
      if (pos === 'sticky' || pos === 'fixed') {
        return Math.round(h.getBoundingClientRect().height) + GAP;
      }
    }
    return GAP;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (k === 'class')      node.className = attrs[k];
        else if (k === 'text')  node.textContent = attrs[k];
        else if (k === 'html')  node.innerHTML = attrs[k];
        else if (k.indexOf('on') === 0 && typeof attrs[k] === 'function') node.addEventListener(k.slice(2), attrs[k]);
        else if (attrs[k] === true)  node.setAttribute(k, '');
        else if (attrs[k] === false || attrs[k] == null) {}
        else node.setAttribute(k, attrs[k]);
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c == null) continue;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return node;
  }

  // Inline SVG strings the theme uses, to avoid an extra Liquid render call.
  var ICON_CHEVRON = '<svg aria-hidden="true" focusable="false" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-chevron-down"><path class="icon-chevron-down-left" d="M4 4.5L7 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path><path class="icon-chevron-down-right" d="M4 4.5L1 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path></svg>';
  var ICON_CHECKMARK = '<svg class="checkmark" aria-hidden="true" focusable="false" role="presentation" width="14" height="14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><path class="checkmark__check" fill="none" d="M2.5 7L5.5 10L11.5 4"></path><path class="checkmark__indeterminate" fill="none" d="M2.5 7L6 7L12 7"></path></svg>';
  // Pulled from snippets/icon-library.liquid id 'icon-remove' so the active
  // filter pill renders the same SVG the native theme would.
  var ICON_REMOVE = '<svg aria-hidden="true" focusable="false" role="presentation" width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M6.08785659,5 L9.77469752,1.31315906 L8.68684094,0.225302476 L5,3.91214341 L1.31315906,0.225302476 L0.225302476,1.31315906 L3.91214341,5 L0.225302476,8.68684094 L1.31315906,9.77469752 L5,6.08785659 L8.68684094,9.77469752 L9.77469752,8.68684094 L6.08785659,5 Z"></path></svg>';

  // ─── Index loader ────────────────────────────────────────────────
  // Server now rebuilds the filter index every 5 minutes (was nightly) and
  // ships it with Cache-Control: max-age=60. To honour that "≤60s in-session
  // staleness" promise, the sessionStorage cache below also enforces a 60s
  // TTL against the payload's own `generated_at` timestamp — otherwise a
  // long-lived tab would pin the first-fetched copy forever regardless of
  // what max-age says.
  var INDEX_TTL_MS = 60 * 1000;

  function loadIndex(url) {
    // Dev-only fixture hook: on localhost, ?indexOverride=<url> swaps the
    // feed source so synthetic payloads (e.g. k-row fixtures) can be tested
    // against the real theme. Bypasses sessionStorage in both directions —
    // fixture data must never mask, or be masked by, a cached real feed.
    // Gated to local hostnames, so production behavior is untouched.
    var host = window.location.hostname;
    if (host === '127.0.0.1' || host === 'localhost') {
      var override = new URLSearchParams(window.location.search).get('indexOverride');
      if (override) {
        return fetch(override, { credentials: 'omit' }).then(function (res) {
          if (!res.ok) throw new Error('Index fetch failed: HTTP ' + res.status);
          return res.json();
        });
      }
    }

    try {
      var cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.products) {
          var generatedAt = parsed.generated_at ? Date.parse(parsed.generated_at) : NaN;
          if (isFinite(generatedAt) && (Date.now() - generatedAt) < INDEX_TTL_MS) {
            return Promise.resolve(parsed);
          }
          // Fall through to refetch — payload is older than the TTL, or
          // lacks a parseable `generated_at` (older cached build).
        }
      }
    } catch (_) {}

    return fetch(url, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Index fetch failed: HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch (_) {}
        return data;
      });
  }

  // ─── Language scope ──────────────────────────────────────────────
  // The singles index is a single shared feed containing BOTH English and
  // Japanese cards. Japanese rows carry `l: "ja"`; English rows have NO `l`
  // key at all — there is no `l: "en"`, English is signalled by absence.
  // This contract is authoritative and per-card upstream (build_filter_index
  // stamps `l` from each card's language_code). Each collection page passes a
  // `data-language` scope so it renders only its own market:
  //   'ja'  → Japanese only  (Pokémon Singles Japan)
  //   'en'  → English only   (Pokémon Singles)
  //   'all' → everything     (default; legacy behaviour, no filtering)
  // The scope governs BROWSE; a typed query on English pages searches the
  // full feed so Japanese cards appear in results (see init's row views).
  // Scoping happens on the in-memory copy, so the shared sessionStorage
  // cache still holds the full feed and the two pages can't poison each
  // other's data.
  function scopeByLanguage(products, language) {
    if (language === 'ja') return products.filter(function (p) { return p.l === 'ja'; });
    if (language === 'en') return products.filter(function (p) { return !p.l; });
    return products;
  }

  // ─── Text search (normalize → index → match) ─────────────────────
  // Match-quality ladder (lower = better), attached to each row as `_m`:
  //   M0 exact  — whole query equals the card name, or a (set code + card
  //               number) token pair hits exactly.
  //   M1 strong — name starts with the query, or every token whole-word
  //               matches (a word of the name / the set code / the card
  //               number).
  //   M2 sub    — every token is a substring of the haystack (AND).
  //   M3 fuzzy1 — M2 after correcting ≥1 token (length ≥4) at Damerau-
  //               Levenshtein distance 1 against the token dictionary.
  //   M4 fuzzy2 — single-token queries (length ≥6) at distance 2.
  // Fuzzy tiers are FALLBACK-ONLY: they run only when every stricter tier
  // matched nothing, so a correctly spelled query never collects typo
  // noise. Fuzzy never applies to set codes, card numbers, or non-ASCII
  // tokens (Japanese queries stay exact-substring).

  var NON_ALNUM_RE = /[^\p{L}\p{N}\p{M}]+/gu;
  var LATIN_MARKS_RE = /[̀-ͯ]/g;

  // lowercase → NFD strip Latin combining marks (é→e; kana dakuten are
  // outside U+0300–U+036F so Japanese text survives) → non-alphanumerics
  // to spaces → collapse runs → trim.
  function normalizeText(x) {
    if (x == null) return '';
    return String(x)
      .toLowerCase()
      .normalize('NFD')
      .replace(LATIN_MARKS_RE, '')
      .replace(NON_ALNUM_RE, ' ')
      .trim();
  }

  // Stamps the per-row derived fields the matcher and sort chains read
  // (_idx/_n/_s/_c/_cn/_hay/_m) and builds the fuzzy-correction dictionary
  // (unique words of card names + set names, bucketed by length so a
  // distance-k lookup only scans buckets within ±k of the token length).
  // MUST be re-run every time the row array is replaced (each loadIndex
  // resolution) — the derived fields live on the row objects themselves.
  function buildSearchIndex(rows) {
    var wordSeen = Object.create(null);
    var nameSeen = Object.create(null);
    var setNameSeen = Object.create(null);
    var setCodes = Object.create(null);
    var words = [];
    function addWords(text) {
      var parts = text.split(' ');
      for (var w = 0; w < parts.length; w++) {
        if (parts[w] && !wordSeen[parts[w]]) { wordSeen[parts[w]] = true; words.push(parts[w]); }
      }
    }
    for (var i = 0; i < rows.length; i++) {
      var p = rows[i];
      p._idx = i;
      p._n = normalizeText(p.n);
      p._s = normalizeText(p.s);
      p._c = normalizeText(p.c);
      var lead = /^\d+/.exec(p._c);
      p._cn = lead ? parseInt(lead[0], 10) : Infinity;
      // k-rows (non-card entries in future unified feeds) index name+type;
      // card rows index name + set name + set code + card number.
      p._hay = p.k
        ? normalizeText((p.n || '') + ' ' + (p.ty || ''))
        : p._n + ' ' + normalizeText(p.sn) + ' ' + p._s + ' ' + p._c;
      p._m = 0;
      // Static per-feed values, precomputed so the per-keystroke hot paths
      // (computeFacets, applyFilters, sort comparators) read properties
      // instead of re-running bucket regexes / the nv-typeof check tens of
      // thousands of times per render.
      p._t = inStockRank(p);
      p._rb = rarityBucket(p.r);
      p._pb = printTypeBucket(p.pt);
      if (p._s) setCodes[p._s] = true;
      if (p._n && !nameSeen[p._n]) { nameSeen[p._n] = true; addWords(p._n); }
      if (p.sn && !setNameSeen[p.sn]) { setNameSeen[p.sn] = true; addWords(normalizeText(p.sn)); }
    }
    var byLen = Object.create(null);
    for (var j = 0; j < words.length; j++) {
      (byLen[words[j].length] || (byLen[words[j].length] = [])).push(words[j]);
    }
    return { byLen: byLen, setCodes: setCodes };
  }

  // Damerau-Levenshtein (adjacent transpositions count 1) with a row-min
  // early exit once the distance can no longer come in under `max`.
  function damerau(a, b, max) {
    var la = a.length, lb = b.length;
    if (la - lb > max || lb - la > max) return max + 1;
    var prevPrev = null, prev = [], cur, i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= lb; j++) {
        var v = Math.min(
          prev[j] + 1,
          cur[j - 1] + 1,
          prev[j - 1] + (a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1)
        );
        if (i > 1 && j > 1 &&
            a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
            a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          var tv = prevPrev[j - 2] + 1;
          if (tv < v) v = tv;
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prevPrev = prev; prev = cur;
    }
    return prev[lb];
  }

  function fuzzyCandidates(token, byLen, maxDist, cap) {
    var out = [];
    for (var len = token.length - maxDist; len <= token.length + maxDist; len++) {
      var bucket = byLen[len];
      if (!bucket) continue;
      for (var i = 0; i < bucket.length; i++) {
        var w = bucket[i];
        if (w !== token && damerau(token, w, maxDist) <= maxDist) {
          out.push(w);
          if (out.length >= cap) return out;
        }
      }
    }
    return out;
  }

  var ASCII_WORD_RE = /^[a-z]+$/;

  // Does this query token name the row's card number? Matches the full
  // normalized number ("199 230"), its leading segment ("199"), or its
  // numeric value (so "5" hits card "005/088").
  function tokenMatchesCard(tok, p) {
    if (!p._c) return false;
    if (tok.t === p._c || p._c.indexOf(tok.t + ' ') === 0) return true;
    return tok.num !== null && tok.num === p._cn;
  }

  // M0–M2 for one row, or -1 when the row fails all exact tiers.
  function matchLevel(p, nq, toks) {
    if (p._n === nq) return 0;
    var i;
    if (toks.length >= 2 && p._s) {
      var sAt = -1;
      for (i = 0; i < toks.length; i++) {
        if (toks[i].t === p._s) { sAt = i; break; }
      }
      if (sAt !== -1) {
        for (i = 0; i < toks.length; i++) {
          if (i !== sAt && tokenMatchesCard(toks[i], p)) return 0;
        }
      }
    }
    if (p._n.lastIndexOf(nq, 0) === 0) return 1;
    var whole = true;
    for (i = 0; i < toks.length; i++) {
      var t = toks[i].t;
      if ((' ' + p._n + ' ').indexOf(' ' + t + ' ') === -1 && t !== p._s && !tokenMatchesCard(toks[i], p)) {
        whole = false; break;
      }
    }
    if (whole) return 1;
    for (i = 0; i < toks.length; i++) {
      if (p._hay.indexOf(toks[i].t) === -1) return -1;
    }
    return 2;
  }

  // Fuzzy AND-check: every token slot must hit via the original token or
  // one of its distance-1 corrections.
  function haystackHasAll(p, cands) {
    for (var t = 0; t < cands.length; t++) {
      var list = cands[t];
      var hit = false;
      for (var c = 0; c < list.length; c++) {
        if (p._hay.indexOf(list[c]) !== -1) { hit = true; break; }
      }
      if (!hit) return false;
    }
    return true;
  }

  // Returns the matched rows for normalized query `nq`, each stamped with
  // its `_m` rank. Rows failing every tier are excluded. `idx` is the
  // dictionary built by buildSearchIndex.
  function matchText(rows, nq, idx) {
    var tokens = nq.split(' ');
    var toks = [];
    for (var k = 0; k < tokens.length; k++) {
      toks.push({ t: tokens[k], num: /^\d+$/.test(tokens[k]) ? parseInt(tokens[k], 10) : null });
    }
    var out = [];
    var i, p;
    for (i = 0; i < rows.length; i++) {
      p = rows[i];
      var m = matchLevel(p, nq, toks);
      if (m !== -1) { p._m = m; out.push(p); }
    }
    if (out.length) return out;

    // M3: distance-1 corrections, only for ASCII word tokens of length ≥4
    // that aren't set codes (numbers never pass ASCII_WORD_RE).
    var anyFix = false;
    var cands = [];
    for (i = 0; i < toks.length; i++) {
      var t = toks[i].t;
      var list = [t];
      if (t.length >= 4 && ASCII_WORD_RE.test(t) && !idx.setCodes[t]) {
        var fixes = fuzzyCandidates(t, idx.byLen, 1, 8);
        if (fixes.length) { anyFix = true; list = list.concat(fixes); }
      }
      cands.push(list);
    }
    if (anyFix) {
      for (i = 0; i < rows.length; i++) {
        p = rows[i];
        if (haystackHasAll(p, cands)) { p._m = 3; out.push(p); }
      }
      if (out.length) return out;
    }

    // M4: distance-2, single ASCII token of length ≥6 only.
    if (toks.length === 1) {
      var t1 = toks[0].t;
      if (t1.length >= 6 && ASCII_WORD_RE.test(t1) && !idx.setCodes[t1]) {
        var far = fuzzyCandidates(t1, idx.byLen, 2, 8);
        if (far.length) {
          for (i = 0; i < rows.length; i++) {
            p = rows[i];
            for (var c = 0; c < far.length; c++) {
              if (p._hay.indexOf(far[c]) !== -1) { p._m = 4; out.push(p); break; }
            }
          }
        }
      }
    }
    return out;
  }

  // ─── State (querystring-synced) ──────────────────────────────────
  // Default sort is price high → low: the page should land on the
  // most valuable singles first; filters refine from there.
  var DEFAULT_SORT = 'price-desc';

  function defaultState() {
    return {
      q: '',
      series: [], sets: [], rarities: [], types: [], prints: [], prices: [],
      // Product type (Singles/Japanese/Sealed/Graded) — search-mode only;
      // cleared whenever the query empties (see rerender).
      ptypes: [],
      inStock: false,
      sort: DEFAULT_SORT,
      // True while the sort was auto-set to relevance by a ?q= deep link
      // (no explicit sort param). An auto sort is never written back to
      // the URL and reverts to DEFAULT_SORT when the search is cleared;
      // a sort the user picked (URL param or dropdown click) persists.
      sortAuto: false,
      page: 1
    };
  }

  function readStateFromUrl() {
    var s = defaultState();
    var p = new URLSearchParams(window.location.search);
    if (p.get('q'))      s.q        = p.get('q').trim();
    if (p.get('series')) s.series   = p.get('series').split(',').filter(Boolean);
    if (p.get('set'))    s.sets     = p.get('set').split(',').filter(Boolean);
    if (p.get('rarity')) s.rarities = p.get('rarity').split(',').filter(Boolean);
    if (p.get('type'))   s.types    = p.get('type').split(',').filter(Boolean);
    if (p.get('print'))  s.prints   = p.get('print').split(',').filter(Boolean);
    if (p.get('price'))  s.prices   = p.get('price').split(',').filter(Boolean);
    if (p.get('ptype'))  s.ptypes   = p.get('ptype').split(',').filter(Boolean);
    if (p.get('stock') === '1') s.inStock = true;
    if (p.get('sort'))   s.sort     = p.get('sort');
    if (p.get('page'))   s.page     = Math.max(1, parseInt(p.get('page'), 10) || 1);
    // A query arriving via URL with no explicit sort lands on relevance —
    // deep links from the header search should read best-match-first. A
    // query TYPED on the page never touches the sort (see init's onSearch).
    if (s.q && !p.get('sort')) { s.sort = 'relevance'; s.sortAuto = true; }
    return s;
  }

  function writeStateToUrl(state) {
    var p = new URLSearchParams();
    // Carry the dev-only ?indexOverride= param (see loadIndex) through
    // state writes, so a reload mid-fixture-test stays on the fixture
    // instead of silently reverting to the real feed.
    var override = new URLSearchParams(window.location.search).get('indexOverride');
    if (override)              p.set('indexOverride', override);
    if (state.q)               p.set('q', state.q);
    if (state.series.length)   p.set('series', state.series.join(','));
    if (state.sets.length)     p.set('set', state.sets.join(','));
    if (state.rarities.length) p.set('rarity', state.rarities.join(','));
    if (state.types.length)    p.set('type', state.types.join(','));
    if (state.prints.length)   p.set('print', state.prints.join(','));
    if (state.prices.length)   p.set('price', state.prices.join(','));
    if (state.ptypes.length)   p.set('ptype', state.ptypes.join(','));
    if (state.inStock)         p.set('stock', '1');
    if (state.sort && state.sort !== DEFAULT_SORT && !state.sortAuto) p.set('sort', state.sort);
    if (state.page > 1)        p.set('page', String(state.page));
    var qs = p.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  // ─── Filter / sort ───────────────────────────────────────────────
  // Text matching happens BEFORE this in the rerender pipeline (matchText,
  // memoized per query) — `products` arrives pre-matched, so this only
  // applies the facet/stock filters.
  function applyFilters(products, state) {
    var seriesSet = state.series.length   ? new Set(state.series)   : null;
    var setSet     = state.sets.length     ? new Set(state.sets)     : null;
    var raritySet  = state.rarities.length ? new Set(state.rarities) : null;
    var typeSet    = state.types.length    ? new Set(state.types)    : null;
    var printSet   = state.prints.length   ? new Set(state.prints)   : null;
    var ptypeSet   = state.ptypes.length   ? new Set(state.ptypes)   : null;
    var priceBands = state.prices.length
      ? PRICE_BUCKETS.filter(function (b) { return state.prices.indexOf(b.label) !== -1; })
      : null;

    var out = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (p.k) {
        // k-rows carry no card facet fields, so any active card-facet
        // filter excludes them outright. Price bands and the in-stock
        // filter below still apply.
        if (seriesSet || setSet || raritySet || typeSet || printSet) continue;
      } else {
        if (seriesSet && !seriesSet.has(p.e || 'Other')) continue;
        if (setSet && !setSet.has(p.s)) continue;
        if (raritySet && !raritySet.has(p._rb || rarityBucket(p.r))) continue;
        if (typeSet && !typeSet.has(p.t)) continue;
        if (printSet && !printSet.has(p._pb || printTypeBucket(p.pt))) continue;
      }
      if (ptypeSet && !ptypeSet.has(productTypeBucket(p))) continue;
      if (priceBands) {
        var hit = false;
        for (var b = 0; b < priceBands.length; b++) {
          if (p.p >= priceBands[b].min && p.p < priceBands[b].max) { hit = true; break; }
        }
        if (!hit) continue;
      }
      if (state.inStock && !p.i) continue;
      out.push(p);
    }
    return out;
  }

  // Stock tier for sorting: in-stock cards always rank ahead of sold-out
  // ones, whatever sort is chosen. Mirrors the `nv`/`i` fallback the card
  // renderer uses (see renderCard) so the tiering and the Sold Out button
  // always agree on what "in stock" means.
  function inStockRank(p) {
    var nv = typeof p.nv === 'number' ? p.nv : (p.i ? 2 : 0);
    return nv > 0 ? 0 : 1;
  }

  // Sort is two-tier: in-stock first, sold-out after, with the chosen sort
  // applied WITHIN each tier. So "price high → low" leads with the dearest
  // in-stock card, then the sold-out block follows in its own high→low
  // order. Every chain ends in `_idx` (unique feed position, stamped by
  // buildSearchIndex), so the full ordering is total and deterministic —
  // no reliance on sort stability. `_m` is the match rank from matchText
  // (0 for every row when the query is empty).
  //
  // Chains: price-asc/desc  tier → p ± → _m → _n → s → _cn → pt → _idx
  //         name-asc/desc   tier → _n ± → _m → s → _cn → pt → _idx
  //         relevance       tier → _m → p desc → _idx
  //         featured        tier → _idx  (feed order within each tier)
  var NAME_COLLATOR = (typeof Intl !== 'undefined' && Intl.Collator)
    ? new Intl.Collator('en', { sensitivity: 'base' })
    : null;

  function cmpName(a, b) {
    if (a._n === b._n) return 0;
    if (NAME_COLLATOR) return NAME_COLLATOR.compare(a._n, b._n);
    return a._n < b._n ? -1 : 1;
  }

  // Comparator-side tier read: `_t` is inStockRank precomputed at index
  // build (stock never changes within one feed load); fall back to the
  // live function for any row that predates the build.
  function tierOf(p) {
    return p._t !== undefined ? p._t : inStockRank(p);
  }

  // Shared trailing tie-break: set code → card number → print type → feed
  // position. `_cn` can be Infinity on both sides (non-numeric card
  // numbers), so compare with <, not subtraction.
  function cmpTail(a, b) {
    var as = a.s || '', bs = b.s || '';
    if (as !== bs) return as < bs ? -1 : 1;
    if (a._cn !== b._cn) return a._cn < b._cn ? -1 : 1;
    var apt = a.pt || '', bpt = b.pt || '';
    if (apt !== bpt) return apt < bpt ? -1 : 1;
    return a._idx - b._idx;
  }

  function applySort(rows, sort) {
    var sorted = rows.slice();
    var cmp;
    if (sort === 'price-asc' || sort === 'price-desc') {
      var pdir = sort === 'price-asc' ? 1 : -1;
      cmp = function (a, b) {
        var tier = tierOf(a) - tierOf(b);
        if (tier) return tier;
        if (a.p !== b.p) return (a.p - b.p) * pdir;
        if (a._m !== b._m) return a._m - b._m;
        var n = cmpName(a, b);
        if (n) return n;
        return cmpTail(a, b);
      };
    } else if (sort === 'name-asc' || sort === 'name-desc') {
      var ndir = sort === 'name-asc' ? 1 : -1;
      cmp = function (a, b) {
        var tier = tierOf(a) - tierOf(b);
        if (tier) return tier;
        var n = cmpName(a, b) * ndir;
        if (n) return n;
        if (a._m !== b._m) return a._m - b._m;
        return cmpTail(a, b);
      };
    } else if (sort === 'relevance') {
      cmp = function (a, b) {
        var tier = tierOf(a) - tierOf(b);
        if (tier) return tier;
        if (a._m !== b._m) return a._m - b._m;
        if (a.p !== b.p) return b.p - a.p;
        return a._idx - b._idx;
      };
    } else { // featured
      cmp = function (a, b) {
        var tier = tierOf(a) - tierOf(b);
        if (tier) return tier;
        return a._idx - b._idx;
      };
    }
    sorted.sort(cmp);
    return sorted;
  }

  function computeFacets(products) {
    var series = Object.create(null),
        sets = Object.create(null), rarities = Object.create(null),
        types = Object.create(null), prints = Object.create(null),
        prices = Object.create(null), ptypes = Object.create(null), stock = 0;
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      // k-rows have none of the card facet fields — they count toward
      // Product type, Price and Availability only.
      if (!p.k) {
        var eb = p.e || 'Other';    series[eb]   = (series[eb] || 0) + 1;
        if (!sets[p.s]) sets[p.s] = { code: p.s, name: p.sn || p.s, count: 0 };
        sets[p.s].count++;
        var rb = p._rb || rarityBucket(p.r); rarities[rb] = (rarities[rb] || 0) + 1;
        var tb = p.t || 'Other';    types[tb]    = (types[tb] || 0) + 1;
        var pb = p._pb || printTypeBucket(p.pt); prints[pb] = (prints[pb] || 0) + 1;
      }
      var kb = productTypeBucket(p); ptypes[kb] = (ptypes[kb] || 0) + 1;
      for (var b = 0; b < PRICE_BUCKETS.length; b++) {
        var band = PRICE_BUCKETS[b];
        if (p.p >= band.min && p.p < band.max) { prices[band.label] = (prices[band.label] || 0) + 1; break; }
      }
      if (p.i) stock++;
    }
    var setList = Object.keys(sets).map(function (k) { return sets[k]; });
    setList.sort(function (a, b) { return a.name.localeCompare(b.name); });
    return { series: series, sets: setList, rarities: rarities, types: types, prints: prints, prices: prices, ptypes: ptypes, stock: stock };
  }

  function toggleArrayValue(arr, value, on) {
    var idx = arr.indexOf(value);
    if (on && idx === -1) arr.push(value);
    if (!on && idx !== -1) arr.splice(idx, 1);
  }

  // ─── Sidebar render (native collection-filters__* markup) ────────
  // Each facet group's counts reflect what would happen if the user toggled
  // a value in that group, given every OTHER active filter. So selecting
  // "Common" in Rarity makes the Price counts shrink to "Commons in each
  // band", but the Rarity group itself still shows the unrestricted-by-
  // rarity counts (so you can switch from Common to Uncommon without
  // first having to untick Common). Standard faceted-search behaviour —
  // matches what Boost / Searchanise / Shopify native do.
  // `matchedProducts` is the query-matched subset from the rerender's
  // single matchText pass — the text match must NOT be re-run per facet
  // group (it used to run 7 extra times per keystroke here).
  function renderFacets(root, matchedProducts, state, onChange, seriesOrder, openGroups) {
    var container = root.querySelector('[data-singles-filter-facets]');
    container.innerHTML = '';

    // Distinct effective-filter states collapse to one computation: with no
    // active filters all 7 groups share one result; with one active group,
    // two. Memo lives per render call, so it can never serve stale counts.
    var facetMemo = Object.create(null);

    function countsExcluding(key) {
      var stateCopy = {
        series: state.series, sets: state.sets, rarities: state.rarities,
        types: state.types, prints: state.prints, prices: state.prices,
        ptypes: state.ptypes,
        inStock: state.inStock, sort: state.sort, page: state.page
      };
      if (key === 'inStock') stateCopy.inStock = false;
      else if (key) stateCopy[key] = [];
      var sig = stateCopy.series.join(',') + '|' + stateCopy.sets.join(',') + '|' +
                stateCopy.rarities.join(',') + '|' + stateCopy.types.join(',') + '|' +
                stateCopy.prints.join(',') + '|' + stateCopy.prices.join(',') + '|' +
                stateCopy.ptypes.join(',') + '|' +
                (stateCopy.inStock ? 1 : 0);
      if (!facetMemo[sig]) {
        facetMemo[sig] = computeFacets(applyFilters(matchedProducts, stateCopy));
      }
      return facetMemo[sig];
    }

    var seriesFacets = countsExcluding('series');
    var typeFacets   = countsExcluding('types');
    var rarityFacets = countsExcluding('rarities');
    var printFacets  = countsExcluding('prints');
    var priceFacets  = countsExcluding('prices');
    var setFacets    = countsExcluding('sets');
    var stockFacets  = countsExcluding('inStock');

    // Search-mode Product type facet — rendered only when the matched set
    // spans more than one bucket (Singles/Japanese/Sealed/Graded), so it
    // never appears as a single-value no-op (e.g. the JP page, where every
    // row buckets as Japanese). Empty-query browse never gets here with
    // non-Singles rows (they're excluded before matching), so the browse
    // sidebar is untouched.
    var firstBucket = null, mixedBuckets = false;
    for (var mb = 0; mb < matchedProducts.length; mb++) {
      var bkt = productTypeBucket(matchedProducts[mb]);
      if (firstBucket === null) firstBucket = bkt;
      else if (bkt !== firstBucket) { mixedBuckets = true; break; }
    }
    if (state.q && mixedBuckets) {
      var ptypeFacets = countsExcluding('ptypes');
      container.appendChild(filterGroup('Product type', ['Singles', 'Japanese', 'Sealed', 'Graded'].map(function (t) {
        return { label: t, value: t, count: ptypeFacets.ptypes[t] || 0, active: state.ptypes.indexOf(t) !== -1 };
      }), function (val, on) {
        toggleArrayValue(state.ptypes, val, on);
        state.page = 1; onChange();
      }, /* scrollable */ false, openGroups));
    }

    // Series + Set sit at the top of the sidebar as the geographic
    // hierarchy: Series (generation) → Set (specific release). Order comes
    // from the payload's top-level series_order array (newest era first,
    // Promo pinned to the end, auto-derived from set release_dates). Any
    // value present in the data but missing from series_order lands
    // alphabetically at the bottom — defensive coverage for partial /
    // mid-deploy payloads only; under normal operation the payload's
    // series_order covers every value in the index.
    var order = (seriesOrder && seriesOrder.length) ? seriesOrder : SERIES_ORDER_FALLBACK;
    var presentSeries = Object.keys(seriesFacets.series);
    var orderedSeries = order.filter(function (s) { return presentSeries.indexOf(s) !== -1; });
    var extraSeries = presentSeries
      .filter(function (s) { return order.indexOf(s) === -1; })
      .sort(function (a, b) { return a.localeCompare(b); });
    var seriesList = orderedSeries.concat(extraSeries);

    container.appendChild(filterGroup('Series', seriesList.map(function (s) {
      return { label: s, value: s, count: seriesFacets.series[s] || 0, active: state.series.indexOf(s) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.series, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Set', setFacets.sets.map(function (s) {
      return { label: s.name, value: s.code, count: s.count, active: state.sets.indexOf(s.code) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.sets, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ true, openGroups));

    container.appendChild(filterGroup('Card type', [
      { label: 'Pokémon',  value: 'Pokémon',  count: typeFacets.types['Pokémon']  || 0, active: state.types.indexOf('Pokémon')  !== -1 },
      { label: 'Trainer',  value: 'Trainer',  count: typeFacets.types['Trainer']  || 0, active: state.types.indexOf('Trainer')  !== -1 }
    ], function (val, on) {
      toggleArrayValue(state.types, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Rarity', RARITY_BUCKETS.map(function (b) {
      return { label: b.label, value: b.label, count: rarityFacets.rarities[b.label] || 0, active: state.rarities.indexOf(b.label) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.rarities, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Print', ['Holo', 'Reverse Holo', 'Non-Holo', '1st Edition', 'Shadowless', 'Special'].map(function (k) {
      return { label: k, value: k, count: printFacets.prints[k] || 0, active: state.prints.indexOf(k) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.prints, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Price', PRICE_BUCKETS.map(function (b) {
      return { label: b.label, value: b.label, count: priceFacets.prices[b.label] || 0, active: state.prices.indexOf(b.label) !== -1 };
    }), function (val, on) {
      toggleArrayValue(state.prices, val, on);
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));

    container.appendChild(filterGroup('Availability', [
      { label: 'In stock', value: 'in', count: stockFacets.stock, active: state.inStock }
    ], function (val, on) {
      state.inStock = on;
      state.page = 1; onChange();
    }, /* scrollable */ false, openGroups));
  }

  // Render one accordion group using the exact native DOM the theme ships with.
  // `openGroups` (optional) is a shared object keyed by group title, persisting
  // user open/close choice across renderFacets re-renders. Without it the group
  // would re-derive its open state from "any active value" on every toggle —
  // which causes the group to collapse the moment the user unchecks the last
  // active value in it.
  function filterGroup(title, values, onToggle, scrollable, openGroups) {
    var anyActive = values.some(function (v) { return v.active; });
    // First-time render: open if any value is active (e.g. shared URL with
    // ?series=XY). Once a render has happened, the stored value wins so the
    // group never auto-collapses on an unrelated re-render.
    var expanded;
    if (openGroups && typeof openGroups[title] === 'boolean') {
      expanded = openGroups[title];
    } else {
      expanded = anyActive;
      if (openGroups) openGroups[title] = expanded;
    }

    var heading = el('button', {
      type: 'button',
      class: 'collection-filters__filter-group-heading productgrid--sidebar-button' + (expanded ? ' productgrid--sidebar-button-active' : ''),
      'aria-expanded': expanded ? 'true' : 'false'
    }, [
      el('div', { class: 'collection-filters__filter-title' }, [title]),
      el('div', { class: 'collection-filters__filter-icon-wrapper', tabindex: '-1' }, [
        el('span', { class: 'collection-filters__filter-icon', html: ICON_CHEVRON })
      ])
    ]);

    var listClasses = 'collection-filters__filter collection-filters__filter-list' + (expanded ? ' filter-group-active' : '');
    var list = el('ul', {
      class: listClasses,
      'data-filter-group-list': '',
      'data-accordion-content': '',
      'data-accordion-state': expanded ? 'open' : 'closed'
    });
    if (!expanded) list.style.display = 'none';

    // Render every value, but mark count==0 (and not already active) as
    // disabled — clicking it would empty the grid, so we let it sit there
    // greyed-out instead of vanishing. Less jarring than hiding/reflowing
    // the list every time the user toggles an adjacent facet.
    values.forEach(function (v) {
      var disabled = !v.count && !v.active;
      list.appendChild(filterItem(v, onToggle, disabled));
    });

    heading.addEventListener('click', function () {
      var nowOpen = heading.getAttribute('aria-expanded') !== 'true';
      heading.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      heading.classList.toggle('productgrid--sidebar-button-active', nowOpen);
      list.classList.toggle('filter-group-active', nowOpen);
      list.setAttribute('data-accordion-state', nowOpen ? 'open' : 'closed');
      list.style.display = nowOpen ? '' : 'none';
      if (openGroups) openGroups[title] = nowOpen;
    });

    if (scrollable) list.classList.add('singles-filter--facet-scroll');

    return el('div', { class: 'collection-filters__filter-group', 'aria-label': title, 'data-filter-group': '' }, [heading, list]);
  }

  function filterItem(v, onToggle, disabled) {
    var liClass = 'collection-filters__filter-list-item';
    if (v.active) liClass += ' collection-filters__filter-list-item--active';
    if (disabled) liClass += ' collection-filters__filter-list-item--disabled';
    var li = el('li', {
      class: liClass,
      'data-filter-open': v.active ? 'true' : 'false'
    });

    var linkAttrs = {
      class: 'collection-filters__filter-link',
      href: '#',
      'data-filter-input': ''
    };
    if (disabled) {
      linkAttrs['data-disabled'] = '';
      linkAttrs['aria-disabled'] = 'true';
      linkAttrs.onclick = function (e) { e.preventDefault(); };
    } else {
      linkAttrs.onclick = function (e) {
        e.preventDefault();
        var willActivate = !v.active;
        onToggle(v.value, willActivate);
      };
    }
    var link = el('a', linkAttrs, [
      el('span', { class: 'filter-icon--checkbox', html: ICON_CHECKMARK }),
      el('span', { class: 'collection-filters__filter-list-item-text' }, [
        v.label + (v.count != null ? ' (' + v.count + ')' : '')
      ])
    ]);

    if (v.active) {
      // Match the theme's checked-state markup so the brand checkmark animates in.
      var svg = link.querySelector('.checkmark');
      if (svg) {
        svg.setAttribute('data-animation-state', 'checked');
        Array.prototype.forEach.call(svg.querySelectorAll('path'), function (p) {
          p.setAttribute('data-animation-state', 'checked');
        });
      }
      link.setAttribute('data-filter-active', '');
    }

    li.appendChild(link);
    return li;
  }

  // ─── Active filter pills ─────────────────────────────────────────
  // Mounts a `.productgrid--filters > ul.active-filter-group` block
  // ONLY when there are active filters, so when the user has nothing
  // selected the slot stays empty and the theme's 28px filter-row
  // margin doesn't push the grid down (parity with the sealed page).
  function renderActivePills(root, state, allFacets, onChange) {
    var host = root.querySelector('[data-singles-filter-active-host]');
    host.innerHTML = '';

    function setNameForCode(code) {
      var entry = allFacets.sets.filter(function (s) { return s.code === code; })[0];
      return entry ? entry.name : code;
    }

    var pills = [];
    function add(label, onRemove) {
      pills.push({ label: label, onRemove: onRemove });
    }

    // Active search term renders as the first pill — removing it clears
    // ONLY the search (input + q + masthead swap, via the rerender) and
    // returns the customer to the filtered browse view they had before
    // searching; every facet pill stays put.
    if (state.q) {
      add('Search: “' + state.q + '”', function () {
        state.q = '';
        var input = document.querySelector('.collection-search-bar input[type="search"], .collection-search-bar input[type="text"]');
        if (input) input.value = '';
        if (state.sortAuto) { state.sort = DEFAULT_SORT; state.sortAuto = false; }
        state.page = 1;
        onChange();
      });
    }

    state.ptypes.forEach(function (t) {
      add(t, function () { toggleArrayValue(state.ptypes, t, false); state.page = 1; onChange(); });
    });
    state.series.forEach(function (s) {
      add(s, function () { toggleArrayValue(state.series, s, false); state.page = 1; onChange(); });
    });
    state.sets.forEach(function (c) {
      add(setNameForCode(c), function () { toggleArrayValue(state.sets, c, false); state.page = 1; onChange(); });
    });
    state.rarities.forEach(function (r) {
      add(r, function () { toggleArrayValue(state.rarities, r, false); state.page = 1; onChange(); });
    });
    state.types.forEach(function (t) {
      add(t, function () { toggleArrayValue(state.types, t, false); state.page = 1; onChange(); });
    });
    state.prints.forEach(function (p) {
      add(p, function () { toggleArrayValue(state.prints, p, false); state.page = 1; onChange(); });
    });
    state.prices.forEach(function (p) {
      add(p, function () { toggleArrayValue(state.prices, p, false); state.page = 1; onChange(); });
    });
    if (state.inStock) {
      add('In stock', function () { state.inStock = false; state.page = 1; onChange(); });
    }

    if (pills.length === 0) return;

    // Native pill structure (mirrors snippets/product-grid-filters-active.liquid)
    // so .active-filter-group__item-wrapper > .active-filter-group__item picks
    // up the brand-blue overrides at theme.css.liquid line 24766.
    //
    // Clear all is rendered FIRST (not last as in the native theme). With many
    // active filters the row fills, and a trailing Clear all wraps onto its
    // own line and looks stranded. Putting it first guarantees it's always
    // visible at top-left of the first row, never below the pills.
    var ul = el('ul', { class: 'active-filter-group' });
    ul.appendChild(el('li', { class: 'active-filter-group--remove-all' }, [
      el('a', {
        class: 'filter-item-clear-all',
        href: '#',
        // Clears EVERYTHING — filters and the search term. Delegated to
        // the root [data-singles-filter-clear] handler (see init), which
        // owns the full reset: state, the search input, and the
        // auto-relevance sort revert. Arriving from a header search
        // leaves only the search chip active, and a Clear all that spared
        // it read as broken (clicking it did visibly nothing). The search
        // chip's × remains the remove-search-only affordance.
        'data-singles-filter-clear': ''
      }, ['Clear all'])
    ]));
    pills.forEach(function (p) {
      var link = el('a', {
        class: 'active-filter-group__item',
        href: '#',
        'data-filter-input': '',
        onclick: function (e) { e.preventDefault(); p.onRemove(); }
      }, [
        el('span', { class: 'active-filter-text' }, [p.label]),
        el('span', { class: 'filter-icon--remove', 'aria-hidden': 'true', html: ICON_REMOVE })
      ]);
      ul.appendChild(el('li', { class: 'active-filter-group__item-wrapper' }, [link]));
    });

    // Use our own class — `productgrid--filters` is hidden by the theme below
    // 860px (theme.css.liquid line 17436), which made the pill row vanish on
    // tablet/mobile. `.singles-filter--active-pills` styles in our CSS block.
    host.appendChild(el('div', { class: 'singles-filter--active-pills' }, [ul]));
  }

  // ─── Card render (native productgrid--item productitem markup) ───
  function renderCards(root, rows, state, perPage) {
    var grid = root.querySelector('[data-singles-filter-grid]');
    grid.removeAttribute('aria-busy');
    grid.innerHTML = '';

    var total = rows.length;
    if (total === 0) {
      // Render the empty state inline as a single grid child instead of a
      // separate sibling — avoids the always-rendered no-results sibling
      // visible at the page bottom.
      grid.appendChild(el('div', { class: 'singles-filter--state singles-filter--state-empty' }, [
        el('h2', { class: 'singles-filter--state-title' }, ['No cards match those filters.']),
        el('button', {
          type: 'button',
          class: 'singles-filter--state-clear',
          'data-singles-filter-clear': '',
          onclick: function () {} // bubbles to root delegate
        }, ['Clear filters'])
      ]));
      return;
    }

    var start = (state.page - 1) * perPage;
    var pageRows = rows.slice(start, start + perPage);

    pageRows.forEach(function (p) { grid.appendChild(renderCard(p)); });

    // Per-row title-height balancing lives in sections/collection-search.liquid.
    // The shared script exposes `window.__balanceTitleHeights` (a debounced
    // scheduler) so JS-rendered grids can request a rebalance after every
    // render — pagination clicks, filter changes, sort changes, initial
    // load. Relying on the script's MutationObserver was unreliable here
    // because the observer fires mid-render, before images decode and
    // titles wrap to their final line counts.
    if (typeof window.__balanceTitleHeights === 'function') {
      window.__balanceTitleHeights();
    }

    // Defensive: images set loading="eager" but decode is still async, and
    // title wrap can shift slightly as the layout settles. Schedule one
    // more rebalance on each not-yet-complete image's load.
    grid.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () {
        if (typeof window.__balanceTitleHeights === 'function') {
          window.__balanceTitleHeights();
        }
      }, { once: true });
    });
  }

  function renderCard(p) {
    var link = '/products/' + encodeURIComponent(p.h);

    // Set badge above the image — same DOM the Liquid card uses
    // (snippets/product-grid-item.liquid), so the existing CSS for
    // `.productitem--set-badge` applies.
    // k-rows: sealed rows get their real set badge once the feed emits `b`
    // for them; until then (and always for graded, which has no set) fall
    // back to the generic Pokémon logo so the badge band is never empty.
    var badgeSrc = p.b || (p.k ? BADGE_PLACEHOLDER : null);
    var setBadge = badgeSrc ? el('div', { class: 'productitem--set-badge', 'aria-hidden': 'true' }, [
      el('img', { src: badgeSrc, alt: p.sn || p.ty || '', loading: 'lazy', decoding: 'async' })
    ]) : null;

    // Real Shopify CDN image. Native files are 1000×1200 (5:6) per HANDOFF.md;
    // we serve a 400px variant by default (with an 800px srcset entry for
    // retina) so the file is ~5× smaller and renders near-instantly on
    // pagination. `loading=eager` skips the IntersectionObserver delay so
    // the browser starts fetching the moment the DOM is inserted — pairs
    // with the small file size to make new-page paint feel snappy. The
    // explicit width/height attrs reserve the layout box so the page
    // doesn't reflow as images decode.
    var imgSrc = shopifyImageWidth(p.img, 400);
    var imgSrcset = shopifyImageWidth(p.img, 400) + ' 1x, ' +
                    shopifyImageWidth(p.img, 800) + ' 2x';
    // alt="" is intentional — the product name is rendered as an <h2> right
    // below this image, so duplicating it as alt creates redundant SR output
    // AND, more visibly, renders as a styled link text under the parent <a>
    // during the brief moment between DOM insertion and image decode. The
    // <a>'s aria-label keeps the link semantically labelled for assistive
    // tech without making alt text the loading-state fallback.
    var imageBlock = el('a', {
      class: 'singles-filter--card-image',
      href: link,
      'aria-label': p.n
    }, [
      el('img', {
        src: imgSrc,
        srcset: imgSrcset,
        alt: '',
        loading: 'eager',
        decoding: 'async',
        fetchpriority: 'high',
        width: '1000',
        height: '1200',
        class: 'singles-filter--card-image-img'
      })
    ]);

    var title = el('h2', { class: 'productitem--title' }, [
      el('a', { href: link }, [p.n])
    ]);

    // Restore the metadata row that the original singles cards showed
    // beneath the title — `{card-number} · {rarity} · {print-type}`. Uses
    // the existing `.productitem--card-number` class so it inherits the
    // grey centred styling already in theme.css.liquid (line 23304).
    // k-rows (sealed/graded) have no set/number/rarity/print — their meta
    // line is the product-type label alone. Box art can be any aspect
    // ratio; the 5:6 image frame letterboxes it via object-fit: contain
    // (theme.css.liquid .singles-filter--card-image-img), so the card
    // layout holds without distortion.
    var metaParts = [];
    if (p.k) {
      if (p.ty) metaParts.push(p.ty);
    } else {
      if (p.c) metaParts.push(p.c);
      if (p.r) metaParts.push(p.r);
      if (p.pt) metaParts.push(printTypeAbbrev(p.pt));
    }
    var metaRow = metaParts.length
      ? el('div', { class: 'productitem--card-number' }, [metaParts.join(' · ')])
      : null;

    var price = el('div', { class: 'price productitem__price' }, [
      el('div', { class: 'price__current', 'data-price-container': '' }, [
        el('span', { class: 'money', 'data-price': '' }, [formatPrice(p.p)])
      ])
    ]);

    var info = el('div', { class: 'productitem--info' }, [title, metaRow, price]);

    // Bottom button — driven by `nv` (in-stock variant count) per HANDOFF:
    //   nv === 0 → Sold Out (grayed <span>, not clickable, no price)
    //   nv === 1 → ADD TO CART | $price
    //   nv >= 2  → ADD TO CART | FROM $price
    // Falls back to `p.i` for older JSON builds that predate the `nv`
    // field — treats any in-stock card as 2+ (FROM) in that case.
    var instockCount = typeof p.nv === 'number' ? p.nv : (p.i ? 2 : 0);

    var atcNode;
    if (instockCount === 0) {
      atcNode = el('span', {
        class: 'productitem--action-trigger productitem--action-atc button-primary singles-filter--price-button disabled',
        'aria-disabled': 'true'
      }, ['Sold Out']);
    } else {
      var atcLabel = instockCount === 1
        ? 'ADD TO CART | ' + formatPrice(p.p)
        : 'ADD TO CART | FROM ' + formatPrice(p.p);
      atcNode = el('a', {
        class: 'productitem--action-trigger productitem--action-atc button-primary singles-filter--price-button',
        href: link
      }, [atcLabel]);
    }

    var actions = el('div', { class: 'productitem--actions' }, [
      el('div', { class: 'productitem--action quickshop-button' }, [
        el('a', {
          class: 'productitem--action-trigger button-secondary singles-filter--view-button',
          href: link
        }, ['View Card'])
      ]),
      el('div', { class: 'productitem--action atc--button' }, [atcNode])
    ]);

    return el('div', {
      class: 'productgrid--item imagestyle--natural show-actions--mobile singles-filter--card',
      'data-product-item': '',
      'data-product-handle': p.h
    }, [
      setBadge,
      el('div', { class: 'productitem', 'data-product-item-content': '' }, [
        el('div', { class: 'productitem__container' }, [imageBlock, info, actions])
      ])
    ]);
  }

  // ─── Pagination (theme's pagination--inner classes) ──────────────
  // The theme renders prev/next as a chevron SVG inside a `pagination--chevron-{left|right}`
  // span, with the visible "Prev"/"Next" text hidden via `font-size: 0`. Mirror that
  // markup so the existing CSS at theme.css.liquid line 24929+ paints the arrows.
  function renderPagination(root, total, state, perPage, onPageChange) {
    var nav = root.querySelector('[data-singles-filter-pagination]');
    var list = root.querySelector('[data-singles-filter-pagination-list]');
    list.innerHTML = '';

    var pageCount = Math.max(1, Math.ceil(total / perPage));
    if (pageCount <= 1) { nav.hidden = true; return; }
    nav.hidden = false;

    var current = Math.min(state.page, pageCount);
    if (current !== state.page) state.page = current;

    function pageItem(label, page, opts) {
      opts = opts || {};
      var li = el('li', {
        class: 'pagination--' + (opts.kind || 'page') + (opts.active ? ' pagination--active' : '')
      });
      var children;
      if (opts.kind === 'previous') {
        children = [
          el('span', { class: 'pagination--chevron-left', 'aria-hidden': 'true', html: ICON_CHEVRON }),
          'Prev'
        ];
      } else if (opts.kind === 'next') {
        children = [
          'Next',
          el('span', { class: 'pagination--chevron-right', 'aria-hidden': 'true', html: ICON_CHEVRON })
        ];
      } else {
        children = [label];
      }
      if (opts.disabled || opts.active) {
        li.appendChild(el('span', { class: 'pagination--item' }, children));
      } else {
        li.appendChild(el('a', {
          class: 'pagination--item',
          href: '#',
          'aria-label': opts.kind === 'previous' ? 'Go to previous page'
            : opts.kind === 'next' ? 'Go to next page'
            : ('Go to page ' + label),
          onclick: function (e) { e.preventDefault(); state.page = page; onPageChange(); }
        }, children));
      }
      return li;
    }

    if (current > 1) list.appendChild(pageItem(null, current - 1, { kind: 'previous' }));
    paginationNumbers(current, pageCount).forEach(function (n) {
      if (n === '…') list.appendChild(pageItem('…', null, { kind: 'ellipsis', disabled: true }));
      else list.appendChild(pageItem(String(n), n, { active: n === current }));
    });
    if (current < pageCount) list.appendChild(pageItem(null, current + 1, { kind: 'next' }));
  }

  function paginationNumbers(current, total) {
    var out = [], pad = 2;
    function push(n) { if (out.indexOf(n) === -1) out.push(n); }
    push(1);
    for (var i = current - pad; i <= current + pad; i++) {
      if (i > 1 && i < total) push(i);
    }
    if (total > 1) push(total);
    out.sort(function (a, b) { return a - b; });
    var withGaps = [];
    for (var j = 0; j < out.length; j++) {
      if (j > 0 && out[j] - out[j - 1] > 1) withGaps.push('…');
      withGaps.push(out[j]);
    }
    return withGaps;
  }


  // ─── Top-bar count + filter-button label ─────────────────────────
  function renderMeta(root, state, total) {
    var label = root.querySelector('[data-singles-filter-button-label]');
    if (label) {
      var n = state.sets.length + state.rarities.length + state.types.length +
              state.prints.length + state.prices.length + state.ptypes.length +
              (state.inStock ? 1 : 0);
      label.textContent = n ? 'Filters (' + n + ')' : 'Filters';
    }
  }

  // ─── Bridge to existing collection-search bar ─────────────────────
  // The page already includes the `collection-search` section above main,
  // which gives us a pre-styled search input. Hijack its native input
  // events to drive our filter; otherwise the user gets a duplicate UI.
  function bindCollectionSearchBar(initialQuery, onSearch) {
    var input = document.querySelector('.collection-search-bar input[type="search"], .collection-search-bar input[type="text"]');
    // No search bar on the page: state.q (from the URL) still drives the
    // grid — there's just no way to type a new query.
    if (!input) return null;
    input.setAttribute('placeholder', 'Search singles by name…');
    // Seed a ?q= deep link into the input before the first rerender so
    // the bar and the grid agree from the first paint. Programmatic value
    // set fires no `input` event, so the predictive flydown stays closed.
    if (initialQuery) input.value = initialQuery;
    input.addEventListener('input', debounce(function () {
      onSearch(input.value.trim());
    }, 180));
    // Block the form submit so it doesn't navigate to /search. Must be
    // capture-phase: Empire's SearchForm binds submit on
    // [data-live-search-form] and navigates by cloning the form and calling
    // .submit() on the clone, so preventDefault alone never stops it — and
    // Empire binds lazily, so bubble-order tricks race. Capture on the form
    // itself runs before Empire's handler regardless of registration order.
    var form = input.closest('form');
    if (form) {
      var lsRoot = form.closest('[data-live-search]');
      var fly = lsRoot ? lsRoot.querySelector('[data-live-search-flydown]') : null;
      // Submit dismisses the flydown, but Empire re-opens it moments
      // later: the debounced keyup from the last typed character fires
      // ~250ms after Enter (placeholder open), and its predictive fetch
      // calls _openFlyDown unconditionally when it resolves — a dark bar
      // pops up under the search bar ~a second after submit. Hard-hide
      // the flydown through that window; typing again lifts the hold so
      // live suggestions keep working.
      var flyHoldTimer = null;
      var releaseFlydown = function () {
        if (flyHoldTimer === null) return;
        clearTimeout(flyHoldTimer);
        flyHoldTimer = null;
        if (fly.getAttribute('data-animation-state') === 'open') {
          fly.setAttribute('data-animation-state', 'closed');
        }
        fly.style.display = '';
      };
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Dismiss Empire's predictive flydown so it doesn't sit over the
        // filtered grid — submitting means "show me the results". Reuse
        // Empire's own Escape path (LiveSearch._closeEsc closes the flydown
        // AND the mobile take-over), with a direct attribute fallback in
        // case that binding is ever absent.
        input.blur();
        var esc = { key: 'Escape', bubbles: true };
        document.dispatchEvent(new KeyboardEvent('keydown', esc));
        document.dispatchEvent(new KeyboardEvent('keyup', esc));
        if (lsRoot) lsRoot.classList.remove('live-search--focused');
        if (fly) {
          if (fly.getAttribute('data-animation-state') === 'open') {
            fly.setAttribute('data-animation-state', 'closed');
          }
          fly.style.display = 'none';
          if (flyHoldTimer) clearTimeout(flyHoldTimer);
          flyHoldTimer = setTimeout(releaseFlydown, 3000);
        }
      }, true);
      if (fly) input.addEventListener('input', releaseFlydown);
    }
    // The caller keeps the element so programmatic clears (Clear filters,
    // the search chip's ×) can empty the input alongside state.q — the
    // rerender pipeline reads state.q, never the input directly.
    return input;
  }

  // Map Shopify's standard sort values (used by the collection-search
  // dropdown buttons) to the JS sort modes the JSON-driven filter supports.
  // The JSON has no creation-date field, so date sorts collapse to featured.
  var SHOPIFY_SORT_MAP = {
    'manual': 'featured',
    'most-relevant': 'relevance',
    'best-selling': 'featured',
    'created-ascending': 'featured',
    'created-descending': 'featured',
    'created': 'featured',
    'price-ascending': 'price-asc',
    'price-descending': 'price-desc',
    'title-ascending': 'name-asc',
    'title-descending': 'name-desc'
  };

  function bindCollectionSearchSort(onSort, state) {
    var dropdown = document.querySelector('[data-sort-dropdown]');
    if (!dropdown) return function () {};

    var buttons = dropdown.querySelectorAll('[data-sort-value]');

    function activate(jsSort) {
      Array.prototype.forEach.call(buttons, function (b) {
        var v = SHOPIFY_SORT_MAP[b.getAttribute('data-sort-value')] || 'featured';
        var isActive = v === jsSort;
        b.classList.toggle('sort-option--active', isActive);
        if (isActive) b.setAttribute('disabled', ''); else b.removeAttribute('disabled');
      });
    }

    Array.prototype.forEach.call(buttons, function (b) {
      // Capture-phase + stopImmediatePropagation: collection-search.liquid
      // binds its own delegated click on the dropdown that sets
      // window.location.href (a full-page reload, which would wipe the
      // client-rendered grid). Killing propagation at the button keeps the
      // sort fully client-side; this bind only runs on grid pages, so
      // non-grid pages keep their native navigation. The open/close toggle
      // lives on [data-sort-toggle] outside the dropdown and is unaffected.
      b.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var v = SHOPIFY_SORT_MAP[b.getAttribute('data-sort-value')] || 'featured';
        activate(v);
        onSort(v);
        // Close the dropdown the same way the existing JS does. Also clear
        // the toggle's active state — the section's document-level close
        // handler would normally do it, but the propagation stop above
        // means it never sees this click.
        dropdown.classList.remove('sort-dropdown--open');
        var toggle = document.querySelector('[data-sort-toggle]');
        if (toggle) toggle.classList.remove('sort-toggle--active');
      }, true);
    });

    // Reflect the current sort (URL value, or DEFAULT_SORT) on first load.
    // Auto-relevance has no dropdown counterpart on collection pages
    // (`most-relevant` is a search-page sort), so no button lights up then.
    activate(state.sort || DEFAULT_SORT);

    // Returned so rerender can re-sync the dropdown after programmatic
    // sort changes (auto-relevance revert on search clear / chip removal).
    return activate;
  }

  // ─── Boot ────────────────────────────────────────────────────────
  function init(root) {
    var indexUrl = root.getAttribute('data-index-url');
    var perPage = parseInt(root.getAttribute('data-products-per-page'), 10) || 24;
    var language = root.getAttribute('data-language') || 'all';
    BADGE_PLACEHOLDER = root.getAttribute('data-badge-placeholder') || '';

    var state = readStateFromUrl();
    var allProducts = [];
    // Ordered views over the rows, rebuilt on every index load:
    //   browseRows — market-scoped card rows only. Empty-query browse and
    //                the browse facets read this, so k-rows (and, on
    //                English pages, Japanese rows) never render or count
    //                without a search.
    //   searchRows — the query candidate pool: IN-STOCK rows only, cards
    //                and k-rows alike — sold-out products never appear in
    //                typed-search results, though they still show in
    //                browse. On English pages it spans ALL languages, so a
    //                typed query surfaces Japanese cards too; the JP page
    //                stays Japanese-only.
    var browseRows = [];
    var searchRows = [];
    // Server-supplied chronological order for the Series facet (see HANDOFF
    // schema, top-level `series_order`). Falls back to SERIES_ORDER_FALLBACK
    // if the payload doesn't ship it (legacy / partial build).
    var seriesOrder = SERIES_ORDER_FALLBACK;
    // Persistent open/closed state per facet group, keyed by group title.
    // Survives the renderFacets innerHTML wipe on every toggle, so groups
    // don't collapse when a user unchecks the last value in them.
    //   undefined → use default (open iff group has any active value)
    //   true      → user opened (or initial-render-from-URL had active)
    //   false     → user closed
    var openGroups = Object.create(null);

    var searchInput = bindCollectionSearchBar(state.q, function (term) {
      state.q = term;
      // Clearing the query reverts an auto-set relevance sort to the
      // default; a sort the user explicitly chose this session persists.
      // Typing a query never changes the current sort.
      if (!term && state.sortAuto) {
        state.sort = DEFAULT_SORT;
        state.sortAuto = false;
      }
      state.page = 1;
      rerender();
    });

    // Masthead swap: while a query is active the (normally hidden)
    // collection masthead shows `Results for "TERM"` with the description
    // suppressed; clearing the query restores the cached original title
    // and re-hides the block (see brand-overrides.css).
    var masthead = root.querySelector('.productgrid--masthead');
    var mastheadTitle = masthead ? masthead.querySelector('.collection--title') : null;
    var mastheadTitleOriginal = mastheadTitle ? mastheadTitle.textContent : '';

    function updateMasthead() {
      if (!mastheadTitle) return;
      if (state.q) {
        mastheadTitle.textContent = 'Results for “' + state.q + '”';
        masthead.classList.add('singles-filter--masthead-search');
      } else {
        mastheadTitle.textContent = mastheadTitleOriginal;
        masthead.classList.remove('singles-filter--masthead-search');
      }
    }

    // Matched-set memo: matchText runs at most ONCE per distinct query per
    // rerender cycle — the grid, the facet counts, and the sort all read
    // the same subset. Invalidated when the row array is replaced (each
    // loadIndex resolution rebuilds the index and clears this).
    var searchIndex = null;
    var matchMemo = { q: null, rows: null };

    function computeMatched(q) {
      var nq = normalizeText(q);
      if (matchMemo.q === nq && matchMemo.rows) return matchMemo.rows;
      var rows;
      if (!nq || !searchIndex) {
        // Empty query: every card row matches at rank 0. `_m` must be
        // reset — rows keep the rank stamped by the previous query
        // otherwise. k-rows sit outside browseRows entirely.
        for (var i = 0; i < browseRows.length; i++) browseRows[i]._m = 0;
        rows = browseRows;
      } else {
        rows = matchText(searchRows, nq, searchIndex);
      }
      matchMemo.q = nq;
      matchMemo.rows = rows;
      return rows;
    }

    function rerender() {
      // Product type is a search-mode-only facet: clearing the query drops
      // it, so browse can never be constrained by a filter whose facet
      // group isn't even rendered.
      if (!state.q && state.ptypes.length) state.ptypes = [];
      var matched = computeMatched(state.q);
      var filtered = applyFilters(matched, state);
      var sorted = applySort(filtered, state.sort);
      var allFacets = computeFacets(browseRows);

      renderFacets(root, matched, state, rerender, seriesOrder, openGroups);
      renderActivePills(root, state, allFacets, rerender);
      renderCards(root, sorted, state, perPage);
      renderPagination(root, sorted.length, state, perPage, function paginate() {
        writeStateToUrl(state);
        renderCards(root, sorted, state, perPage);
        renderPagination(root, sorted.length, state, perPage, paginate);
        // Park the page so the search/sort bar sits near the top, just under
        // the header. The product masthead is hidden on these pages (see
        // brand-overrides.css), so .collection-search-wrapper is the first
        // visible content — anchor on it, falling back to the grid root if
        // it's somehow absent. Offset by the live header height + gap.
        var anchor = document.querySelector('.collection-search-wrapper') || root;
        var top = anchor.getBoundingClientRect().top + window.scrollY - stickyHeaderOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
      renderMeta(root, state, sorted.length);
      updateMasthead();
      syncSortUI(state.sort || DEFAULT_SORT);
      writeStateToUrl(state);
    }

    // Hijack the existing collection-search sort dropdown. Its buttons emit
    // standard Shopify sort values (manual, title-ascending, etc.) — we
    // map those to the JS sort modes the JSON supports.
    var syncSortUI = bindCollectionSearchSort(function (value) {
      state.sort = value;
      state.sortAuto = false; // an explicit pick persists across search clears
      state.page = 1;
      rerender();
    }, state);

    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-singles-filter-clear]');
      if (!t) return;
      e.preventDefault();
      // This control clears everything, search included — so the q-cleared
      // sort rule applies: an auto-set relevance sort reverts to the
      // default, an explicitly chosen sort survives.
      var keepSort = state.sortAuto ? DEFAULT_SORT : state.sort;
      Object.assign(state, defaultState());
      state.sort = keepSort;
      if (searchInput) searchInput.value = '';
      rerender();
    });

    loadIndex(indexUrl)
      .then(function (data) {
        allProducts = data.products || [];
        // Rebuild derived per-row fields + fuzzy dictionary on EVERY
        // resolution — the 60s TTL means a later load can hand back a
        // fresh row array with none of the stamped fields on it. Built on
        // the FULL feed (all languages) so cross-market search rows carry
        // `_hay`/`_t` too; the browse/search views below read `_t`.
        searchIndex = buildSearchIndex(allProducts);
        var marketRows = scopeByLanguage(allProducts, language);
        browseRows = marketRows.filter(function (p) { return !p.k; });
        // English pages search the whole catalogue — Japanese cards ride
        // along in typed-query results (browse stays market-scoped, and
        // the JP page stays Japanese-only in both modes).
        var searchBase = language === 'ja' ? marketRows : allProducts;
        searchRows = searchBase.filter(function (p) { return tierOf(p) === 0; });
        matchMemo.q = null;
        matchMemo.rows = null;
        if (Array.isArray(data.series_order) && data.series_order.length) {
          seriesOrder = data.series_order;
        }
        rerender();
      })
      .catch(function (err) {
        console.error('[singles-filter] failed to load index', err);
        var grid = root.querySelector('[data-singles-filter-grid]');
        grid.removeAttribute('aria-busy');
        grid.innerHTML = '';
        grid.appendChild(el('div', { class: 'singles-filter--state singles-filter--state-error' }, [
          el('p', null, ['Couldn’t load the singles index.']),
          el('p', { class: 'singles-filter--state-error-detail' }, [String(err.message || err)])
        ]));
      });
  }

  function boot() {
    var root = document.querySelector('[data-singles-filter-root]');
    if (root) init(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
